import React from 'react';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import _ from 'lodash';
import { removeObjectAtIndex } from '@utils/helper-utils';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { IAccountCurrency, ITransactionFormState } from '..';
import { useMutation } from '@apollo/client';
import { convertAccountToSystemCurrency } from '../queries';
import { isEmpty } from '@utils/validationUtils';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import { accountCurrencyInitialValues } from '../content';
import { MAIN_THEME } from '@styles';
import { lighten } from '@mui/system';

const useStyles = makeStyles()(() => ({
  container: {
    boxShadow: `0px 0px 10px ${MAIN_THEME.color.white92}`,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    paddingTop: '11px',
    marginBottom: '20px',
    marginTop: '20px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '4.0fr 3.0fr 3.0fr 3.0fr 0.25fr',
    gridColumnGap: '5px',
    padding: '7px 5px 7px 15px',
  },
  gridItem: {
    display: 'grid',
    gridTemplateColumns: '6fr 6fr',
    gridColumnGap: '5px',
  },
  singleGridItem: {
    display: 'grid',
    gridTemplateColumns: '2fr',
  },
  titlesContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '4.5px',
    paddingBottom: '4.5px',
    lineHeight: '21px',
  },
  titles: {
    paddingTop: '15px',
    paddingBottom: '13px',
    lineHeight: '21px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  firstTitles: {
    display: 'flex',
    justifyContent: 'center',
  },
  span: {
    fontSize: MAIN_THEME.fontSize.rg,
    fontFamily: MAIN_THEME.font.bold,
  },
  accountCurrencyTitle: {
    backgroundColor: lighten(MAIN_THEME.color.red, 0.9),
    color: MAIN_THEME.color.red,
  },
  accountCurrencyTitles: {
    color: MAIN_THEME.color.red,
  },
  systemCurrencyBaseTitle: {
    backgroundColor: MAIN_THEME.color.currencyLightGreen,
    color: MAIN_THEME.color.currencyGreen,
  },
  systemCurrencyBaseTitles: {
    color: MAIN_THEME.color.currencyGreen,
  },
  systemCurrencySecondaryTitle: {
    backgroundColor: MAIN_THEME.color.currencyLightBlue,
    color: MAIN_THEME.color.currencyBlue,
  },
  systemCurrencySecondaryTitles: {
    color: MAIN_THEME.color.currencyBlue,
  },
  currencySymbol: {
    color: MAIN_THEME.color.red,
  },
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    padding: '0',
    margin: '21px auto 25px',
    display: 'block',
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    display: 'block',
    padding: 0,
    margin: 'auto',
    height: '24px',
  },
  containerStyles: {
    paddingTop: 0,
    minHeight: '32px',
  },
  evenRow: {
    backgroundColor: MAIN_THEME.color.white98,
  },
  oddRow: {},
  inputContainer: {
    height: '30px !important',
    '& .MuiInputBase-root': {
      minHeight: '30px !important',
    },
  },
  selectInputContainer: {
    height: '28px',
    '& .MuiInputBase-root': {
      minHeight: '28px !important',
    },
  },
}));

interface IAccountCurrencyProps {
  systemCurrency: string;
  systemSecondaryCurrency: string;
  systemSecondaryCurrencyCode: string;
  transactionDate: Date;
  pageState: ITransactionFormState;
  onPageStateUpdate: (pageState: ITransactionFormState) => void;
  lovs: {
    companies: Record<string, string>;
    account: Record<string, string>;
    currencies: Record<string, { id: string; symbol: string }>;
  };
}

const AccountCurrency: React.FC<IAccountCurrencyProps> = ({
  systemCurrency,
  systemSecondaryCurrency,
  systemSecondaryCurrencyCode,
  transactionDate,
  pageState,
  onPageStateUpdate,
  lovs,
}) => {
  const { classes } = useStyles();

  const [convertAccountToSystem] = useMutation(
    convertAccountToSystemCurrency()
  );

  const values = pageState.values.account;
  const errors = pageState.errors.account;
  const touched = pageState.touched.account;

  const convertAccountToSys = async (
    debit: number,
    credit: number,
    rowIndex: number,
    localPageState = pageState,
    accountName = '',
    currencies: Record<
      string,
      { id: string; symbol: string }
    > = lovs.currencies,
    localTransactionDate?: Date
  ) => {
    const newPageState = _.cloneDeep(localPageState);
    const res = await convertAccountToSystem({
      variables: {
        transactionDate: localTransactionDate ?? transactionDate,
        accountDebitAmount: Number(debit),
        accountCreditAmount: Number(credit),
        accountCurrency: [currencies?.[accountName]?.id],
        SystemSecondaryCurrency: [systemSecondaryCurrencyCode],
      },
      errorPolicy: 'all',
    });

    if (res) {
      const convertedValues =
        res?.data?.accounting?.actions?.convertAccountToSystemCurrency;

      // rowValues.accountDebit = debit;
      // rowValues.accountCredit = credit;
      newPageState.values.account[rowIndex].systemDebit =
        convertedValues?.SystemDebit || 0;
      newPageState.values.account[rowIndex].systemCredit =
        convertedValues?.SystemCredit || 0;
      newPageState.values.account[rowIndex].systemSecondaryDebit =
        convertedValues?.SystemSecondaryDebit || 0;
      newPageState.values.account[rowIndex].systemSecondaryCredit =
        convertedValues?.SystemSecondaryCredit || 0;

      if (!isEmpty(accountName)) {
        newPageState.values.account[rowIndex].accountName = accountName;
      }

      if (isEmpty(res.errors)) {
        if (newPageState.errors.account[rowIndex]) {
          newPageState.errors.account[rowIndex][
            debit !== 0 ? `accountDebit` : `accountCredit`
          ] = '';
        }
      } else {
        const newError = res.errors[0].message;

        if (debit !== 0) {
          if (newPageState.errors.account[rowIndex]) {
            newPageState.errors.account[rowIndex].accountDebit = newError;
            newPageState.errors.account[rowIndex].accountCredit = null;
          }
        }

        if (credit !== 0) {
          if (newPageState.errors.account[rowIndex]) {
            newPageState.errors.account[rowIndex].accountCredit = newError;
            newPageState.errors.account[rowIndex].accountDebit = null;
          }
        }
      }

      onPageStateUpdate(newPageState);
    }
  };

  const onFieldBlur = (fieldName: string, rowIndex: number) => {
    const newPageState = _.cloneDeep(pageState);
    const accountRow = newPageState.values.account[rowIndex];

    if (fieldName === 'accountDebit') {
      // accountRow.accountCredit = 0;
      // accountRow.accountDebit = accountRow.accountDebit);

      convertAccountToSys(
        accountRow.accountDebit,
        0,
        rowIndex,
        pageState,
        pageState.values.account[rowIndex].accountName,
        lovs.currencies,
        transactionDate
      );
    }

    if (fieldName === 'accountCredit') {
      // accountRow.accountDebit = 0;
      // accountRow.accountCredit = trimTrailingZeros(accountRow.accountCredit);

      convertAccountToSys(
        0,
        accountRow.accountCredit,
        rowIndex,
        pageState,
        pageState.values.account[rowIndex].accountName,
        lovs.currencies,
        transactionDate
      );
    }

    if (fieldName === 'accountName') {
      // accountRow.accountCredit = 0;
      // accountRow.accountDebit = 0;
      // convertAccountToSys(0, 0, rowIndex);
    }

    if (newPageState.touched.account[rowIndex]) {
      newPageState.touched.account[rowIndex][fieldName] = true;
    }
    onPageStateUpdate(newPageState);
  };

  const onFieldUpdate = (
    fieldName: keyof IAccountCurrency,
    value: any,
    rowIndex: number,
    touched = false
  ) => {
    const newPageState = _.cloneDeep(pageState);
    (newPageState.values.account[rowIndex] as any)[fieldName] = value;
    const accountRow = newPageState.values.account[rowIndex];

    if (touched) {
      newPageState.touched.account[rowIndex][fieldName] = true;
    }

    if (fieldName === 'accountName') {
      accountRow.accountCredit = 0;
      accountRow.accountDebit = 0;
      accountRow.accountDebit = 0;
      accountRow.systemDebit = 0;
      accountRow.systemCredit = 0;
      accountRow.systemSecondaryDebit = 0;
      accountRow.systemSecondaryCredit = 0;
      convertAccountToSys(0, 0, rowIndex, newPageState, value);
    }

    if (fieldName === 'accountDebit') {
      accountRow.accountCredit = 0;
    }

    if (fieldName === 'accountCredit') {
      accountRow.accountDebit = 0;
    }

    onPageStateUpdate(newPageState);
  };

  const getAccountCurrency = (rowIndex: number) => {
    if (
      !isEmpty(lovs.currencies) &&
      !isEmpty(pageState.values.account[rowIndex].accountName)
    ) {
      return lovs.currencies[pageState.values.account[rowIndex].accountName]
        ?.symbol;
    }
    return '';
  };

  const renderRow = (row: IAccountCurrency, index: number) => {
    const filteredPlanLov = { ...lovs.account };
    const rowIndex = values.indexOf(row);

    return (
      <div
        key={`${values[rowIndex]?.accountName}-index-${index}`}
        className={clsx(
          'container',
          classes.grid,
          rowIndex % 2 === 0 ? classes.evenRow : classes.oddRow
        )}
      >
        <div className={clsx('item', classes.gridItem)}>
          <SelectFormField
            key="accountName"
            name="accountName"
            showTooltipOnDisabled
            title={''}
            inputOnly
            hideError
            value={values[rowIndex]?.accountName}
            error={
              touched[rowIndex]?.accountName ? errors[rowIndex].accountName : ''
            }
            onChange={(v) => {
              onFieldUpdate('accountName', v, rowIndex);
            }}
            disabled={
              !pageState?.values?.transaction?.company ||
              !pageState?.values?.transaction?.date
            }
            selectOptions={filteredPlanLov}
            classes={{
              input: classes.selectInputContainer,
            }}
          />
          <TextInputFormField
            key="description"
            name="description"
            title=""
            inputOnly
            hideError
            value={values[rowIndex]?.description || ''}
            error={
              touched[rowIndex]?.description ? errors[rowIndex].description : ''
            }
            onBlur={() => onFieldBlur('description', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('description', v.target.value, rowIndex)
            }
            disabled={
              !pageState?.values?.transaction?.company ||
              !pageState?.values?.transaction?.date
            }
            classes={{
              input: classes.inputContainer,
            }}
          />
        </div>
        <div className={clsx('item', classes.gridItem)}>
          <CurrencyFormField
            inputOnly
            hideError
            key="accountDebit"
            name="accountDebit"
            title=""
            value={values[rowIndex]?.accountDebit}
            error={
              touched[rowIndex]?.accountDebit
                ? errors[rowIndex]?.accountDebit
                : ''
            }
            onBlur={() => onFieldBlur('accountDebit', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('accountDebit', v.target.value, rowIndex)
            }
            disabled={!values[rowIndex].accountName}
            maxDecimalPrecision={3}
            currencySymbol={getAccountCurrency(rowIndex)}
            classes={{
              currencySymbol: classes.currencySymbol,
              inputContainer: classes.inputContainer,
            }}
          />
          <CurrencyFormField
            inputOnly
            hideError
            key="accountCredit"
            name="accountCredit"
            title=""
            value={values[rowIndex]?.accountCredit}
            error={
              touched[rowIndex]?.accountCredit
                ? errors[rowIndex]?.accountCredit
                : ''
            }
            onBlur={() => onFieldBlur('accountCredit', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('accountCredit', v.target.value, rowIndex)
            }
            classes={{
              currencySymbol: classes.currencySymbol,
              inputContainer: classes.inputContainer,
            }}
            disabled={!values[rowIndex]?.accountName}
            maxDecimalPrecision={3}
            currencySymbol={getAccountCurrency(rowIndex)}
          />
        </div>
        <div className={clsx('item', classes.gridItem)}>
          <CurrencyFormField
            inputOnly
            hideError
            key="systemDebit"
            name="systemDebit"
            title=""
            maxDecimalPrecision={3}
            currencySymbol={systemCurrency}
            value={values[rowIndex]?.systemDebit}
            error={
              touched[rowIndex]?.systemDebit
                ? errors[rowIndex]?.systemDebit
                : ''
            }
            onBlur={() => onFieldBlur('systemDebit', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('systemDebit', v.target.value, rowIndex)
            }
            classes={{
              currencySymbol: classes.currencySymbol,
              inputContainer: classes.inputContainer,
            }}
            disabled
          />
          <CurrencyFormField
            inputOnly
            hideError
            key="systemCredit"
            name="systemCredit"
            title=""
            maxDecimalPrecision={3}
            currencySymbol={systemCurrency}
            value={values[rowIndex]?.systemCredit}
            error={
              touched[rowIndex]?.systemCredit
                ? errors[rowIndex]?.systemCredit
                : ''
            }
            onBlur={() => onFieldBlur('systemCredit', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('systemCredit', v.target.value, rowIndex)
            }
            classes={{
              currencySymbol: classes.currencySymbol,
              inputContainer: classes.inputContainer,
            }}
            disabled
          />
        </div>
        <div className={clsx('item', classes.gridItem)}>
          <CurrencyFormField
            inputOnly
            hideError
            key="systemSecondaryDebit"
            name="systemSecondaryDebit"
            title=""
            maxDecimalPrecision={3}
            currencySymbol={systemSecondaryCurrency}
            value={values[rowIndex]?.systemSecondaryDebit}
            error={
              touched[rowIndex]?.systemSecondaryDebit
                ? errors[rowIndex]?.systemDebit
                : ''
            }
            onBlur={() => onFieldBlur('systemDebit', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('systemDebit', v.target.value, rowIndex)
            }
            classes={{
              currencySymbol: classes.currencySymbol,
              inputContainer: classes.inputContainer,
            }}
            disabled
          />
          <CurrencyFormField
            inputOnly
            hideError
            key="systemSecondaryCredit"
            name="systemSecondaryCredit"
            title=""
            maxDecimalPrecision={3}
            currencySymbol={systemSecondaryCurrency}
            value={values[rowIndex]?.systemSecondaryCredit}
            error={
              touched[rowIndex]?.systemSecondaryCredit
                ? errors[rowIndex]?.systemSecondaryCredit
                : ''
            }
            onBlur={() => onFieldBlur('systemCredit', rowIndex)}
            onChange={(v) =>
              onFieldUpdate('systemCredit', v.target.value, rowIndex)
            }
            disabled
            classes={{
              currencySymbol: classes.currencySymbol,
              inputContainer: classes.inputContainer,
            }}
          />
        </div>
        <div className={clsx('item', classes.singleGridItem)}>
          {values.length > 2 && (
            <button
              className={classes.removeBtn}
              onClick={() => onRowDelete(rowIndex)}
            >
              <ClearIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const onRowAdd = () => {
    const newPageState = _.cloneDeep(pageState);
    newPageState.values.account.push({
      id: null,
      accountName: '',
      description: '',
      accountDebit: 0,
      accountCredit: 0,
      systemDebit: 0,
      systemCredit: 0,
      systemSecondaryDebit: 0,
      systemSecondaryCredit: 0,
    });

    newPageState.errors.account.push({
      ...accountCurrencyInitialValues.initialError,
    });

    newPageState.touched.account.push({
      ...accountCurrencyInitialValues.initialTouched,
    });

    onPageStateUpdate(newPageState);
  };

  const onRowDelete = (rowIndex: number) => {
    const newPageState = _.cloneDeep(pageState);
    newPageState.values.account = removeObjectAtIndex(
      newPageState.values.account,
      rowIndex
    );
    newPageState.errors.account = removeObjectAtIndex(
      newPageState.errors.account,
      rowIndex
    );
    newPageState.touched.account = removeObjectAtIndex(
      newPageState.touched.account,
      rowIndex
    );
    newPageState.values.delete.push(values[rowIndex]?.id);
    onPageStateUpdate(newPageState);
  };

  const shouldDisplayPlus = () => {
    const filteredPlanLov = { ...lovs.account };

    values.forEach((r) => {
      if (r.accountName) {
        delete filteredPlanLov[r.accountName];
      }
    });

    return Object.keys(filteredPlanLov).length > 0;
  };

  return (
    <div className={classes.container}>
      <div className={clsx('container', classes.grid)}>
        <div className="item"></div>
        <div className="item">
          <div
            className={clsx(
              classes.titlesContainer,
              classes.accountCurrencyTitle
            )}
          >
            <span className={classes.span}>Account Currency</span>
          </div>
        </div>
        <div className="item">
          <div
            className={clsx(
              classes.titlesContainer,
              classes.systemCurrencyBaseTitle
            )}
          >
            <span className={classes.span}>System Currency (Basic)</span>
          </div>
        </div>
        <div className="item">
          <div
            className={clsx(
              classes.titlesContainer,
              classes.systemCurrencySecondaryTitle
            )}
          >
            <span className={classes.span}>System Currency (Secondary)</span>
          </div>
        </div>
        <div className="item"></div>
      </div>
      <div className={clsx('container', classes.grid)}>
        <div className={clsx('item', classes.gridItem)}>
          <div className={clsx(classes.titles, classes.firstTitles)}>
            <span className={classes.span}>Account*</span>
          </div>
          <div className={clsx(classes.titles, classes.firstTitles)}>
            <span className={classes.span}>Description*</span>
          </div>
        </div>
        <div className={clsx('item', classes.gridItem)}>
          <div className={clsx(classes.titles, classes.accountCurrencyTitles)}>
            <span className={classes.span}>Debit</span>
          </div>
          <div className={clsx(classes.titles, classes.accountCurrencyTitles)}>
            <span className={classes.span}>Credit</span>
          </div>
        </div>
        <div className={clsx('item', classes.gridItem)}>
          <div
            className={clsx(classes.titles, classes.systemCurrencyBaseTitles)}
          >
            <span className={classes.span}>Debit</span>
          </div>
          <div
            className={clsx(classes.titles, classes.systemCurrencyBaseTitles)}
          >
            <span className={classes.span}>Credit</span>
          </div>
        </div>
        <div className={clsx('item', classes.gridItem)}>
          <div
            className={clsx(
              classes.titles,
              classes.systemCurrencySecondaryTitles
            )}
          >
            <span className={classes.span}>Debit</span>
          </div>
          <div
            className={clsx(
              classes.titles,
              classes.systemCurrencySecondaryTitles
            )}
          >
            <span className={classes.span}>Credit</span>
          </div>
        </div>
        <div></div>
      </div>
      {values.map((v, index) => renderRow(v, index))}

      <button
        className={classes.addBtn}
        onClick={() => onRowAdd()}
        disabled={!shouldDisplayPlus()}
      >
        <AddCircleOutlineIcon
          color={!shouldDisplayPlus() ? 'disabled' : 'error'}
          fontSize="medium"
        />
      </button>
    </div>
  );
};

export default AccountCurrency;
